import { createSelector } from '@reduxjs/toolkit';

import { assertNonNullable, requireNonNullable } from '@disy/lib/custom-error';
import { uniqueId } from '@disy/lib/unique-id';

import type { LayerId } from 'cadenza/workbook/api/layer-id';
import type { ViewDto } from 'cadenza/workbook/api/view-dto';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { WorkbookLayerDto } from 'cadenza/workbook/api/workbook-layer-dto';
import type { WorkbookLayerTreeNode } from 'cadenza/workbook/api/workbook-layer-tree-node';
import { isUnreferencedLayer } from 'cadenza/workbook/map/workbook-layer-utils';
import { getViewById } from 'cadenza/workbook/workbook-core-selectors.state';

export const MAXIMUM_DEPTH = 5; // 1 for root + 3 groups + 1 layer on the bottom

export type ExtendedLayerNode = WorkbookLayerTreeNode & {
  parent?: ExtendedLayerNode;
  depth: number;
  children: ExtendedLayerNode[];
};

export const getViewTreeModel = createSelector(getViewById, (view) => createViewTreeModel(view));

/**
 * Creates a model for all the layers of a view. The root node is a placeholder representing the view.
 *
 * @param view
 */
function createViewTreeModel(view: ViewDto) {
  const rootId = ('view_root_' + uniqueId()) as LayerId;
  return new WorkbookLayerTreeModel(
    {
      id: rootId,
      children: view.layerTree,
    },
    { [rootId]: { structureType: 'GROUP' }, ...view.layerDefinitions },
  );
}

/**
 * A model of layer tree providing utility methods.
 */
export class WorkbookLayerTreeModel {
  readonly #layerDefinitions: Record<LayerId, WorkbookLayerDto>;
  readonly #allNodesFlat: ExtendedLayerNode[];

  constructor(rootNode: WorkbookLayerTreeNode, layerDefinitions: Record<LayerId, WorkbookLayerDto>) {
    this.#layerDefinitions = layerDefinitions;
    this.#allNodesFlat = flattenAndEnrichTree(rootNode);
  }

  get rootNode() {
    return this.#allNodesFlat[0];
  }

  get layerDefinitions() {
    return this.#layerDefinitions;
  }

  get rootId() {
    return this.rootNode.id;
  }

  getLayerNode(layerId: LayerId) {
    return this.#allNodesFlat.find((currentNode) => currentNode.id === layerId);
  }

  getRequiredLayerNode(layerId: LayerId): ExtendedLayerNode {
    return requireNonNullable(this.getLayerNode(layerId));
  }

  getLayerDefinition(layerId: LayerId) {
    return this.#layerDefinitions[layerId];
  }

  /**
   * Checks if a layer is visible.
   * A layer is considered visible if its own settings contain a true value for the visible key,
   * and all of its ancestors (parent groups) are also visible.
   * @param nodeId
   */
  isLayerNodeVisible(nodeId: LayerId): boolean {
    const layerNode = this.getLayerNode(nodeId);
    return Boolean(layerNode && this.#isNodeVisible(layerNode));
  }

  /**
   * Checks if a layer is root node.
   * @param nodeId
   */
  isRootNode(nodeId: LayerId) {
    return this.rootId === nodeId;
  }

  /**
   * For a given layer that we want to move to another group in the tree (or to the root), returns
   * a list of layer ids where this layer can be moved into.
   * If the list is empty then the move action should not be available at all.
   * Restrictions on moving:
   * - Can move only to a group layer (this potentially includes the root node of a view)
   * - Can't move if after the move depth of the layer tree would be greater than MAXIMUM_DEPTH
   * - Can't move to current parent - it's already in this place
   * - Can't move to itself or to any of its children (subgroups)
   *
   */
  getLayerMoveTargets(layerToBeMovedId: LayerId, viewId: ViewId): LayerId[] {
    const layerToBeMoved = this.getLayerNode(layerToBeMovedId)!;
    const layerToBeMovedDepth = this.getSubtreeHeight(layerToBeMoved);
    return this.#allNodesFlat
      .filter((potentialTargetNode, index) => {
        if (!this.isGroupLayer(potentialTargetNode.id)) {
          return false;
        }
        const targetGroupLayer = this.#layerDefinitions[potentialTargetNode.id];
        const isUnreferencedGroupLayer =
          targetGroupLayer?.status === 'OK' && isUnreferencedLayer(targetGroupLayer, viewId);

        const isRootNode = index === 0;
        return (
          (isRootNode || isUnreferencedGroupLayer) &&
          potentialTargetNode.depth + layerToBeMovedDepth <= MAXIMUM_DEPTH &&
          potentialTargetNode.id !== layerToBeMoved.parent?.id &&
          !isNodeInSubtree(potentialTargetNode.id, layerToBeMoved)
        );
      })
      .map((node) => node.id);
  }

  /**
   * Returns number of levels in the subtree.
   *
   * @param layer
   */
  getSubtreeHeight(layer: ExtendedLayerNode) {
    let maxDepth = 1;
    traverseTreeGeneric(layer, (node, depth) => {
      if (depth > maxDepth) {
        maxDepth = depth;
      }
    });
    return maxDepth;
  }

  isGroupLayer(layerId: LayerId) {
    return this.getLayerStructureType(layerId) !== 'SINGLE';
  }

  /**
   * Returns the number of children of the specified node's parent, including the node itself.
   * Throws an error if called on the root node.
   *
   * @param layerId
   */
  getNumberOfSiblings(layerId: LayerId) {
    const parent = this.getRequiredLayerNode(layerId).parent;
    assertNonNullable(parent);
    return parent.children.length;
  }

  /**
   * Returns the rank of the specified node within its parent's children list.
   * Note: it starts at 1, not 0, for the first child.
   *
   * Throws an error if called on a root node.
   *
   * @param layerId
   */
  getRankInParent(layerId: LayerId) {
    const parent = this.getRequiredLayerNode(layerId).parent;
    assertNonNullable(parent);
    const index = parent.children.findIndex((node) => node.id === layerId);
    if (index === -1) {
      // we don;t want to return 0 in this case!
      throw new Error('Unexpected error - node could not be found in its own parent!');
    }
    return index + 1;
  }

  /**
   * Returns the ids of the layers defined within the layer's parent group.
   *
   * @param layerId
   */
  getLayerOrderInParent(layerId: LayerId) {
    const parent = this.getRequiredLayerNode(layerId).parent;
    assertNonNullable(parent, 'Layer order should be called only for layer that have parent defined');
    return parent.children.map((layer) => layer.id);
  }

  getLayerStructureType(layerId: LayerId) {
    return this.#layerDefinitions[layerId].structureType;
  }

  /**
   * Returns all the descendants of the node specified but its ID, not including the node itself.
   * @param layerId
   */
  getAllDescendants(layerId: LayerId) {
    const result: (WorkbookLayerTreeNode | ExtendedLayerNode)[] = [];
    if (!this.getLayerNode(layerId)) {
      return result;
    }
    const stack = [...this.getRequiredLayerNode(layerId).children];
    while (stack.length) {
      const current = stack.pop()!;
      for (const child of current.children || []) {
        stack.push(child);
      }
      result.push(current);
    }
    return result;
  }

  /**
   * Returns all the ancestors of given node up to the root, not including the node itself and not including the root.
   * @param layerId
   */
  getAllAncestors(layerId: LayerId) {
    const ancestors: ExtendedLayerNode[] = [];
    const startLayer = this.getLayerNode(layerId);
    if (!startLayer) {
      return ancestors;
    }
    const rootId = this.rootId;
    let parent = startLayer.parent;
    while (parent) {
      if (rootId !== parent.id) {
        ancestors.push(parent);
      }
      parent = parent.parent;
    }
    return ancestors;
  }

  #isNodeVisible(node: ExtendedLayerNode): boolean {
    const layer = this.#layerDefinitions[node.id];

    // if settings.visible is false, it is not visible
    if (!layer.settings?.visible) {
      return false;
    }

    // if settings.visible is true and it is a 1st level node, it is visible
    if (node.depth === 2) {
      return true;
    }

    // if settings.visible is true and it is part of a group, we return visibility of parent group
    return this.#isNodeVisible(node.parent!);
  }
}

function enrichNode(node: WorkbookLayerTreeNode, parentNode?: ExtendedLayerNode): ExtendedLayerNode {
  return {
    id: node.id,
    children: node.children as ExtendedLayerNode[],
    depth: parentNode ? parentNode.depth + 1 : 1,
    parent: parentNode,
  };
}

/**
 * Enrich the basic layer tree DTOs with more useful data such as depth and reference to the parent node
 * and return a list of all the enriched nodes in a depth-first order
 * (most relevant about the order is that the root node will always be at position 0).
 *
 * Uses a non-recursive traversal for better performance.
 *
 * @param rootNode - Root of the tree
 */
function flattenAndEnrichTree(rootNode: WorkbookLayerTreeNode): ExtendedLayerNode[] {
  const allExtendedNodes: ExtendedLayerNode[] = [];
  const stack: { node: WorkbookLayerTreeNode; parent?: ExtendedLayerNode }[] = [{ node: rootNode, parent: undefined }];

  while (stack.length) {
    const current = stack.pop()!;
    const currentExtended = enrichNode(current.node, current.parent);
    allExtendedNodes.push(currentExtended);
    for (const child of current.node.children || []) {
      stack.push({ node: child, parent: currentExtended });
    }
  }

  return allExtendedNodes;
}

/**
 * Checks if a given node, specified by its ID, is included in a specified subtree.
 *
 * @param nodeId - The node to check
 * @param subtreeRoot - The subtree that might contain the node
 */
function isNodeInSubtree(nodeId: LayerId, subtreeRoot: WorkbookLayerTreeNode) {
  let match = false;
  traverseTreeGeneric(subtreeRoot, (visitedNode) => {
    if (visitedNode.id === nodeId) {
      match = true;
    }
  });
  return match;
}

type NodeProcessor = (node: WorkbookLayerTreeNode, depth: number, parent?: WorkbookLayerTreeNode) => void;

/**
 * Generic depth-first non-recursive traversal of a layer tree starting at the root node.
 *
 * @param rootNode - The root node where to start
 * @param processNode - A function to be executed for each node
 */
function traverseTreeGeneric(rootNode: WorkbookLayerTreeNode, processNode: NodeProcessor) {
  const stack: { node: WorkbookLayerTreeNode; parent?: WorkbookLayerTreeNode; depth: number }[] = [
    { node: rootNode, parent: undefined, depth: 1 },
  ];

  while (stack.length) {
    const current = stack.pop()!;
    for (const child of current.node.children || []) {
      stack.push({ node: child, parent: current.node, depth: current.depth + 1 });
    }
    processNode(current.node, current.depth, current.parent);
  }
}
